import { Fragment, createElement, type ReactNode } from 'react';
import { catalog } from './messages';
import { locale, pseudo } from './locale';
import type { Locale, PluralForms } from './types';

export type MessageKey = keyof typeof catalog;

/**
 * Um por idioma, para que `translate('en', …)` nos testes use as regras do inglês mesmo quando a
 * sessão está em português. Entre pt e en as categorias coincidem, mas depender disso seria uma
 * armadilha para o terceiro idioma.
 */
const PLURALS: Record<Locale, Intl.PluralRules> = {
  pt: new Intl.PluralRules('pt-BR'),
  en: new Intl.PluralRules('en-US'),
};

// ------------------------------------------------------------- tipos dos marcadores

/** Extrai `count` e `first` de `'Faltam {count} buracos, a partir do {first}'`. */
type Placeholders<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | Placeholders<Rest>
  : never;

/**
 * Todos os modelos de uma entrada — inclusive **cada forma plural**, não só a `other`. Há mensagem
 * cujo singular nomeia algo que o plural não menciona ("Falta o buraco {first}" contra "Faltam
 * {count} buracos"), e unir só pela `other` deixaria `{first}` sem tipo.
 */
type AllTemplates<V> = V extends string
  ? V
  : V extends PluralForms
    ? Extract<V[keyof V], string>
    : never;

type ParamNames<K extends MessageKey> = Placeholders<AllTemplates<(typeof catalog)[K]['pt']>>;

/** Verdadeiro quando a entrada declara formas plurais em vez de um texto único. */
type IsPlural<K extends MessageKey> = (typeof catalog)[K]['pt'] extends string ? false : true;

/**
 * Sem marcador, `t(key)` não aceita segundo argumento; com marcador, ele passa a ser obrigatório.
 *
 * **Entrada plural sempre exige `count`**, mesmo quando nenhuma das formas o interpola — é ele que
 * escolhe a forma. "Pedido recebido" / "Pedidos recebidos" não têm marcador nenhum, e sem esta
 * regra o compilador aceitaria `t(key)` e a tela mostraria sempre o plural, em silêncio.
 */
type Params<K extends MessageKey> = IsPlural<K> extends true
  ? [params: Record<ParamNames<K> | 'count', string | number>]
  : [ParamNames<K>] extends [never]
    ? []
    : [params: Record<ParamNames<K>, string | number>];

/**
 * As chaves **sem marcador** — as que `t(key)` traduz com um argumento só.
 *
 * É o tipo das tabelas de rótulo (`Record<Status, PlainMessageKey>` e afins): guardar a chave em vez
 * do texto mantém o `satisfies` cobrando exaustividade, e este tipo impede que alguém guarde ali
 * uma chave que exigiria interpolação e só quebraria no ponto de uso.
 */
export type PlainMessageKey = {
  [K in MessageKey]: IsPlural<K> extends true ? never : [ParamNames<K>] extends [never] ? K : never;
}[MessageKey];

/** As chaves **sem formas plurais**, com ou sem marcador — o que o `tx` sabe montar. */
type NonPluralKey = { [K in MessageKey]: IsPlural<K> extends true ? never : K }[MessageKey];

// ----------------------------------------------------------------------- tradução

/**
 * A tradução pura, com o idioma explícito. Exportada separada do `t` porque `locale` é congelado na
 * sessão — é por aqui que os testes verificam os dois idiomas.
 */
/**
 * Escolhe o modelo: a string, ou a forma plural que a categoria pedir.
 *
 * É função à parte, e não um ternário embutido, porque o estreitamento tem de acontecer sobre um
 * **parâmetro**. Embutido, o compilador via o catálogo concreto — hoje sem nenhuma entrada plural —
 * e concluía que o ramo plural era inalcançável, apontando o erro para `entry.other` em vez de para
 * a causa.
 */
function pickTemplate(entry: string | PluralForms, rule: Intl.LDMLPluralRule): string {
  if (typeof entry === 'string') return entry;
  return entry[rule] ?? entry.other;
}

export function translate(
  loc: Locale,
  key: MessageKey,
  params?: Record<string, string | number>,
): string {
  const rule = PLURALS[loc].select(Number(params?.count ?? 0));
  const template = pickTemplate(catalog[key][loc], rule);

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    params && name in params ? String(params[name]) : match,
  );
}

/**
 * O tradutor da aplicação.
 *
 * É função de módulo, não hook: o texto de usuário também nasce fora de componentes — no cliente de
 * API, nos filtros, no cartão de compartilhamento que desenha em canvas. Um idioma só para os dois
 * casos significa que o diff de um componente é a troca das strings, e nada mais.
 *
 * A interpolação é `String(value)` de propósito, sem formatar número: as contagens deste app são
 * buracos, voltas e amigos, e nunca chegam ao separador de milhar. Medida que precisa de formato
 * passa por `fmtInt`/`fmtDecimal` no ponto de uso, que é onde dá para ver se é contagem ou medida.
 */
export function t<K extends MessageKey>(key: K, ...args: Params<K>): string {
  const text = translate(locale, key, args[0] as Record<string, string | number> | undefined);
  return pseudo ? `⟦${text}⟧` : text;
}

/**
 * Como o `t`, mas os marcadores aceitam nós do React — para a frase que traz um trecho em destaque.
 *
 * O destaque vem da **posição do marcador**, não de marcação dentro da mensagem: assim o negrito
 * pode mudar de lugar entre os idiomas sem que a mensagem carregue `<strong>`.
 *
 * Só aceita chave **sem formas plurais**: escolher a forma exige o `count`, que aqui seria um
 * `ReactNode` e não um número. Sem esta restrição, uma entrada plural passaria e mostraria sempre
 * a forma `other`, em silêncio.
 *
 * No pseudo-locale **cada trecho de texto** é envolvido separadamente, e não a frase inteira. É o
 * que mantém a varredura no navegador honesta: ela olha nó de texto por nó de texto, e um `⟦` solto
 * num nó vizinho a faria denunciar como esquecido justamente o texto que está traduzido. De quebra
 * o valor interpolado fica **fora** dos colchetes, que é a verdade — ele é dado, não copy.
 */
export function tx<K extends NonPluralKey>(
  key: K,
  params: Record<ParamNames<K>, ReactNode>,
): ReactNode {
  const template = pickTemplate(catalog[key][locale], 'other');
  const parts: string[] = template.split(/(\{\w+\})/g);

  return createElement(
    Fragment,
    null,
    ...parts.map((part, index) => {
      const name = /^\{(\w+)\}$/.exec(part)?.[1];
      if (name === undefined) return pseudo && part ? `⟦${part}⟧` : part;
      const value = (params as Record<string, ReactNode>)[name];
      return createElement(Fragment, { key: index }, value ?? part);
    }),
  );
}
